import React, { useState, useEffect } from 'react';
import useSortable from '../hooks/useSortable';
import SortableHeader from '../components/SortableHeader';
import { Plus, X, Check, XCircle, AlertTriangle, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api/axios';
import useAuthStore from '../store/authStore';
import VoiceInput from '../components/VoiceInput';
import UiDateRangeBlock from '../components/ui/UiDateRangeBlock';
import UiSearchField from '../components/ui/UiSearchField';
import { formatApiError } from '../utils/apiErrors';
import useDateFilter from '../hooks/useDateFilter';
import { buildStandardPresetOptions } from '../utils/dateRangeUi';
import { formatDateTime } from '../utils/dateFormat';
import { formatUiCurrencyCompact, formatUiDateTimeRange } from '../utils/uiFormat';
import { UiSummaryCard, UiSummaryCards } from '../components/ui/UiSummaryCard';

const formatDateValueLocal = (date) => `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
const addCalendarDays = (dateStr, days) => {
  if (!dateStr) return '';
  const date = new Date(`${dateStr}T12:00:00`);
  if (Number.isNaN(date.getTime())) return '';
  date.setDate(date.getDate() + days);
  return formatDateValueLocal(date);
};

const Wastage = ({ embedded = false }) => {
  const [wastages, setWastages] = useState([]);
  const [rawMaterials, setRawMaterials] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const {
    preset: datePreset,
    setPreset: setDatePreset,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    startTime,
    setStartTime,
    endTime,
    setEndTime,
    shiftRange,
    applyPreset,
  } = useDateFilter('month', 6);
  const filterEndCalendarDate = endDate ? addCalendarDays(endDate, 1) : '';
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    wastage_type: 'expired',
    raw_material: '',
    menu_item: '',
    quantity: 0,
    reason: ''
  });
  const [itemType, setItemType] = useState('raw_material');

  const { hasPermission } = useAuthStore();

  const wastageTypes = [
    { value: 'expired', label: 'Expired' },
    { value: 'damaged', label: 'Damaged' },
    { value: 'customer_return', label: 'Customer Return' },
    { value: 'production_error', label: 'Production Error' },
    { value: 'spillage', label: 'Spillage' }
  ];

  useEffect(() => {
    api.get('/departments/?page_size=500').then(r => setDepartments(r.data.results || r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    fetchData();
  }, [statusFilter, typeFilter, departmentFilter, startDate, endDate]);

  const fetchData = async () => {
    try {
      let url = '/wastages/?';
      if (statusFilter) url += `status=${statusFilter}&`;
      if (typeFilter) url += `wastage_type=${typeFilter}&`;
      if (departmentFilter) url += `department_id=${departmentFilter}&`;
      if (startDate) url += `start_date=${startDate}&`;
      if (endDate) url += `end_date=${endDate}&`;
      
      const [wastageRes, rawRes, menuRes] = await Promise.all([
        api.get(url),
        api.get('/raw-materials/?active=true'),
        api.get('/menu-items/?active=true')
      ]);
      setWastages(wastageRes.data.results || wastageRes.data);
      setRawMaterials(rawRes.data.results || rawRes.data);
      setMenuItems(menuRes.data.results || menuRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(formatApiError(error, 'Failed to load wastage data'));
    } finally {
      setLoading(false);
    }
  };

  const { sortKey: wSortKey, sortDir: wSortDir, handleSort: wHandleSort, sortData: wSortData } = useSortable('created_at', 'desc');

  const filteredWastages = wSortData(wastages.filter(w => {
    const itemName = w.raw_material_name || w.menu_item_name || '';
    return itemName.toLowerCase().includes(searchQuery.toLowerCase());
  }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        raw_material: itemType === 'raw_material' ? formData.raw_material : null,
        menu_item: itemType === 'menu_item' ? formData.menu_item : null
      };
      await api.post('/wastages/', data);
      toast.success('Wastage reported successfully');
      setShowModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to report wastage'));
    }
  };

  const handleApprove = async (id, action) => {
    try {
      await api.post(`/wastages/${id}/approve/`, { action });
      toast.success(action === 'approve' ? 'Wastage approved' : 'Wastage rejected');
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to update wastage'));
    }
  };

  const resetForm = () => {
    setFormData({
      wastage_type: 'expired',
      raw_material: '',
      menu_item: '',
      quantity: 0,
      reason: ''
    });
    setItemType('raw_material');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-700';
      case 'rejected': return 'bg-red-100 text-red-700';
      default: return 'bg-yellow-100 text-yellow-700';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'expired': return 'bg-orange-100 text-orange-700';
      case 'damaged': return 'bg-red-100 text-red-700';
      case 'customer_return': return 'bg-blue-100 text-blue-700';
      case 'production_error': return 'bg-purple-100 text-purple-700';
      case 'spillage': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const pendingCount = wastages.filter(w => w.status === 'pending').length;
  const totalCost = wastages.filter(w => w.status === 'approved').reduce((sum, w) => sum + parseFloat(w.cost_impact || 0), 0);
  const formatCurrency = (value) => formatUiCurrencyCompact(value);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {embedded ? (
        hasPermission('wastage', 'add') && (
          <div className="flex justify-end">
            <button onClick={() => setShowModal(true)} className="bg-primary-600 hover:bg-primary-700 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center gap-2 text-sm sm:text-base">
              Report Wastage
            </button>
          </div>
        )
      ) : (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Wastage Management</h1>
            <p className="text-sm sm:text-base text-gray-500">Track and manage wastage entries</p>
          </div>
          {hasPermission('wastage', 'add') && (
            <button onClick={() => setShowModal(true)} className="bg-primary-600 hover:bg-primary-700 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center gap-2 text-sm sm:text-base">
              Report Wastage
            </button>
          )}
        </div>
      )}

      {/* Filters */}
      <UiDateRangeBlock
        preset={datePreset}
        presetOptions={buildStandardPresetOptions(['today', 'week', 'month', 'year', 'custom'])}
        onPresetChange={(value) => { setDatePreset(value); applyPreset(value); }}
        onPrevious={() => shiftRange(-1)}
        onNext={() => shiftRange(1)}
        fromDate={startDate}
        onFromDateChange={setStartDate}
        toDate={filterEndCalendarDate}
        onToDateChange={(value) => setEndDate(addCalendarDays(value, -1))}
        fromTime={startTime}
        onFromTimeChange={setStartTime}
        toTime={endTime}
        onToTimeChange={setEndTime}
        rangeText={`Selected range: ${formatUiDateTimeRange(startDate, startTime, filterEndCalendarDate, endTime)}`}
      />

      <div className="bg-white rounded-xl shadow-sm p-3 sm:p-4">
        <UiSearchField value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search wastages..." />
      </div>

      <div className="bg-white rounded-xl shadow-sm p-3 sm:p-4">
        <div className="flex flex-wrap gap-2 items-center">
          <select value={departmentFilter} onChange={(e) => setDepartmentFilter(e.target.value)} className="px-3 py-2 border border-gray-200 rounded-lg text-sm">
            <option value="">All Departments</option>
            {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-3 py-2 border border-gray-200 rounded-lg text-sm">
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="px-3 py-2 border border-gray-200 rounded-lg text-sm">
            <option value="">All Types</option>
            {wastageTypes.map(t => (<option key={t.value} value={t.value}>{t.label}</option>))}
          </select>
        </div>
      </div>

      {/* Summary Cards */}
      <UiSummaryCards cols="grid-cols-2 md:grid-cols-3">
        <UiSummaryCard label="Pending" value={pendingCount} tone="text-yellow-600" />
        <UiSummaryCard label="Total Entries" value={wastages.length} />
        <UiSummaryCard label="Cost Impact" value={formatCurrency(totalCost)} tone="text-red-600" />
      </UiSummaryCards>

      {/* Wastage Table - Desktop */}
      <div className="hidden md:block bg-white rounded-xl shadow-sm overflow-hidden overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <SortableHeader label="Wastage #" col="wastage_number" sortKey={wSortKey} sortDir={wSortDir} onSort={wHandleSort} />
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Item</th>
              <SortableHeader label="Type" col="wastage_type" sortKey={wSortKey} sortDir={wSortDir} onSort={wHandleSort} />
              <SortableHeader label="Quantity" col="quantity" sortKey={wSortKey} sortDir={wSortDir} onSort={wHandleSort} />
              <SortableHeader label="Cost Impact" col="cost_impact" sortKey={wSortKey} sortDir={wSortDir} onSort={wHandleSort} />
              <SortableHeader label="Status" col="status" sortKey={wSortKey} sortDir={wSortDir} onSort={wHandleSort} />
              <SortableHeader label="Reported By" col="reported_by_name" sortKey={wSortKey} sortDir={wSortDir} onSort={wHandleSort} />
              <SortableHeader label="Date" col="created_at" sortKey={wSortKey} sortDir={wSortDir} onSort={wHandleSort} />
              <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {filteredWastages.map((wastage) => (
              <tr key={wastage.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-medium text-gray-800">{wastage.wastage_number}</td>
                <td className="px-4 py-3">
                  <div>
                    <p className="font-medium">{wastage.raw_material_name || wastage.menu_item_name}</p>
                    <p className="text-xs text-gray-500">{wastage.raw_material ? 'Raw Material' : 'Menu Item'}</p>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 text-xs rounded-full capitalize ${getTypeColor(wastage.wastage_type)}`}>
                    {wastage.wastage_type?.replace('_', ' ')}
                  </span>
                </td>
                <td className="px-4 py-3">{wastage.quantity}</td>
                <td className="px-4 py-3 text-red-600 font-medium">{formatCurrency(wastage.cost_impact)}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 text-xs rounded-full capitalize ${getStatusColor(wastage.status)}`}>
                    {wastage.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm">{wastage.reported_by_name}</td>
                <td className="px-4 py-3 text-sm">{wastage.created_at ? formatDateTime(wastage.created_at) : '-'}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-2">
                    {wastage.status === 'pending' && hasPermission('wastage', 'approve') && (
                      <>
                        <button onClick={() => handleApprove(wastage.id, 'approve')} className="p-2 text-green-600 hover:bg-green-50 rounded-lg" title="Approve">
                          <Check size={16} />
                        </button>
                        <button onClick={() => handleApprove(wastage.id, 'reject')} className="p-2 text-red-600 hover:bg-red-50 rounded-lg" title="Reject">
                          <XCircle size={16} />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredWastages.length === 0 && (
          <div className="text-center py-12 text-gray-500">No wastage entries found</div>
        )}
      </div>

      {/* Wastage Cards - Mobile */}
      <div className="md:hidden space-y-3">
        {filteredWastages.map((wastage) => (
          <div key={wastage.id} className="bg-white rounded-xl shadow-sm p-4 border">
            <div className="flex items-start justify-between mb-2">
              <div>
                <p className="font-semibold text-gray-800">{wastage.raw_material_name || wastage.menu_item_name}</p>
                <p className="text-xs text-gray-500">{wastage.wastage_number}</p>
              </div>
              <span className={`px-2 py-1 text-xs rounded-full capitalize ${getStatusColor(wastage.status)}`}>
                {wastage.status}
              </span>
            </div>
            <div className="flex flex-wrap gap-2 mb-3">
              <span className={`px-2 py-1 text-xs rounded-full capitalize ${getTypeColor(wastage.wastage_type)}`}>
                {wastage.wastage_type?.replace('_', ' ')}
              </span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-sm mb-3">
              <div>
                <p className="text-xs text-gray-500">Qty</p>
                <p className="font-semibold">{wastage.quantity}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Cost</p>
                <p className="font-semibold text-red-600">{formatCurrency(wastage.cost_impact)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Date</p>
                <p className="font-medium">{wastage.created_at ? formatDateTime(wastage.created_at) : '-'}</p>
              </div>
            </div>
            <p className="text-xs text-gray-500 mb-3">By: {wastage.reported_by_name}</p>
            {wastage.status === 'pending' && hasPermission('wastage', 'approve') && (
              <div className="flex gap-2 pt-2 border-t">
                <button onClick={() => handleApprove(wastage.id, 'approve')} className="flex-1 py-2 text-sm text-green-600 bg-green-50 rounded-lg font-medium flex items-center justify-center gap-1">
                  Approve
                </button>
                <button onClick={() => handleApprove(wastage.id, 'reject')} className="flex-1 py-2 text-sm text-red-600 bg-red-50 rounded-lg font-medium flex items-center justify-center gap-1">
                  Reject
                </button>
              </div>
            )}
          </div>
        ))}
        {filteredWastages.length === 0 && (
          <div className="text-center py-12 text-gray-500 bg-white rounded-xl">No wastage entries found</div>
        )}
      </div>

      {/* Add Wastage Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-4 sm:p-6 max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg sm:text-xl font-bold">Report Wastage</h3>
              <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-gray-700"><X size={24} /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Wastage Type *</label>
                <select required value={formData.wastage_type} onChange={(e) => setFormData({ ...formData, wastage_type: e.target.value })} className="w-full px-3 py-2 border rounded-lg">
                  {wastageTypes.map(t => (<option key={t.value} value={t.value}>{t.label}</option>))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Item Type *</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2">
                    <input type="radio" name="itemType" checked={itemType === 'raw_material'} onChange={() => setItemType('raw_material')} className="text-primary-600" />
                    Raw Material
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="radio" name="itemType" checked={itemType === 'menu_item'} onChange={() => setItemType('menu_item')} className="text-primary-600" />
                    Menu Item
                  </label>
                </div>
              </div>
              {itemType === 'raw_material' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Raw Material *</label>
                  <select required value={formData.raw_material} onChange={(e) => setFormData({ ...formData, raw_material: e.target.value })} className="w-full px-3 py-2 border rounded-lg">
                    <option value="">Select Item</option>
                    {rawMaterials.map(m => (<option key={m.id} value={m.id}>{m.name} ({m.quantity} {m.unit_abbr})</option>))}
                  </select>
                  {formData.raw_material && (() => {
                    const mat = rawMaterials.find(m => String(m.id) === String(formData.raw_material));
                    return mat ? (
                      <div className="mt-2 flex items-center gap-2 text-sm">
                        <span className="text-gray-500">Unit:</span>
                        <span className="font-semibold text-blue-700 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded">{mat.unit_abbr || 'N/A'}</span>
                        <span className="text-gray-400 text-xs">Current stock: {mat.quantity} {mat.unit_abbr}</span>
                      </div>
                    ) : null;
                  })()}
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Menu Item *</label>
                  <select required value={formData.menu_item} onChange={(e) => setFormData({ ...formData, menu_item: e.target.value })} className="w-full px-3 py-2 border rounded-lg">
                    <option value="">Select Item</option>
                    {menuItems.map(m => (<option key={m.id} value={m.id}>{m.name}</option>))}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Quantity *
                  {itemType === 'raw_material' && formData.raw_material && (() => {
                    const mat = rawMaterials.find(m => String(m.id) === String(formData.raw_material));
                    return mat?.unit_abbr ? <span className="ml-2 text-xs font-semibold text-blue-600 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded">{mat.unit_abbr}</span> : null;
                  })()}
                </label>
                <VoiceInput type="number" step="0.01" required value={formData.quantity} onChange={(e) => setFormData({ ...formData, quantity: e.target.value })} placeholder="0" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason *</label>
                <VoiceInput isTextarea required value={formData.reason} onChange={(e) => setFormData({ ...formData, reason: e.target.value })} placeholder="Describe the reason for wastage..." rows={3} />
              </div>
              <div className="flex gap-2 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
                <button type="submit" className="flex-1 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">Submit</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Wastage;
